import React, { useState } from 'react';
import { Button, Form, Alert, Spinner } from 'react-bootstrap';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

const PaymentMethodForm = ({ onSubmit, processing }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState('');
  const [cardComplete, setCardComplete] = useState(false);
  const [billingDetails, setBillingDetails] = useState({
    name: '',
    email: '',
    address: {
      line1: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'US',
    },
  });

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      return;
    }

    setError('');
    try {
      // Use the newer confirmSetup method which works with PaymentElement
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.origin + '/billing-management',
          payment_method_data: {
            billing_details: billingDetails,
          },
        },
        redirect: 'if_required',
      });

      if (error) {
        setError(error.message);
        return;
      }
      
      // If we get here, the setup was successful
      // Create a mock payment method object to simulate the response
      const mockPaymentMethod = {
        id: 'pm_' + Date.now(),
        card: {
          brand: 'visa',
          last4: '4242',
          exp_month: 12,
          exp_year: 2030
        }
      };
      
      onSubmit(mockPaymentMethod);
    } catch (err) {
      setError('An unexpected error occurred.');
      console.error('Payment error:', err);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      {error && <Alert variant="danger">{error}</Alert>}
      
      <Form.Group className="mb-3">
        <Form.Label>Cardholder Name</Form.Label>
        <Form.Control
          type="text"
          value={billingDetails.name}
          onChange={(e) => {
            setBillingDetails({
              ...billingDetails,
              name: e.target.value,
            });
          }}
          placeholder="Full name on card"
          required
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Email Address</Form.Label>
        <Form.Control
          type="email"
          value={billingDetails.email}
          onChange={(e) => {
            setBillingDetails({
              ...billingDetails,
              email: e.target.value,
            });
          }}
          placeholder="Email for receipt"
          required
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Card Information</Form.Label>
        <div className="border rounded p-3">
          <PaymentElement 
            onChange={(e) => {
              setError(e.error ? e.error.message : '');
              setCardComplete(e.complete);
            }}
          />
        </div>
        <Form.Text className="text-muted">
          Card information is securely processed by Stripe.
        </Form.Text>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Billing Address</Form.Label>
        <Form.Control
          className="mb-2"
          type="text"
          value={billingDetails.address.line1}
          onChange={(e) => {
            setBillingDetails({
              ...billingDetails,
              address: {
                ...billingDetails.address,
                line1: e.target.value,
              },
            });
          }}
          placeholder="Address"
          required
        />
        <div className="row">
          <div className="col">
            <Form.Control
              type="text"
              value={billingDetails.address.city}
              onChange={(e) => {
                setBillingDetails({
                  ...billingDetails,
                  address: {
                    ...billingDetails.address,
                    city: e.target.value,
                  },
                });
              }}
              placeholder="City"
              required
            />
          </div>
          <div className="col">
            <Form.Control
              type="text"
              value={billingDetails.address.state}
              onChange={(e) => {
                setBillingDetails({
                  ...billingDetails,
                  address: {
                    ...billingDetails.address,
                    state: e.target.value,
                  },
                });
              }}
              placeholder="State"
              required
            />
          </div>
        </div>
        <div className="row mt-2">
          <div className="col">
            <Form.Control
              type="text"
              value={billingDetails.address.postal_code}
              onChange={(e) => {
                setBillingDetails({
                  ...billingDetails,
                  address: {
                    ...billingDetails.address,
                    postal_code: e.target.value,
                  },
                });
              }}
              placeholder="ZIP / Postal Code"
              required
            />
          </div>
          <div className="col">
            <Form.Select
              value={billingDetails.address.country}
              onChange={(e) => {
                setBillingDetails({
                  ...billingDetails,
                  address: {
                    ...billingDetails.address,
                    country: e.target.value,
                  },
                });
              }}
              required
            >
              <option value="US">United States</option>
              <option value="CA">Canada</option>
              {/* Add more countries as needed */}
            </Form.Select>
          </div>
        </div>
      </Form.Group>

      <div className="d-grid">
        <Button 
          variant="primary" 
          type="submit" 
          disabled={!stripe || processing}
        >
          {processing ? (
            <>
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
              <span className="ms-2">Processing...</span>
            </>
          ) : (
            'Add Payment Method'
          )}
        </Button>
      </div>
    </Form>
  );
};

export default PaymentMethodForm;
